import React, { FC, useMemo } from 'react';
import { required, SimpleFormIterator, ArrayInput, useTranslate, BooleanInput } from 'react-admin';
import { AmountInput } from '~/components/ra';
import { l } from '~/utils/solutions';
import { useStyles } from '~/hooks/useSolutionsStyles';
import Alert from '@material-ui/lab/Alert';
import { SelectInput, FormDataConsumer } from 'react-admin';
import { H3 } from '~/components';
import { JsonInput } from './JsonInput';
import { Button } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { useProductsBySolutionId } from '~/hooks/product';
import { useForm } from 'react-final-form';
import { ProductTemplate, Solution } from '~/types';
import { useProductTemplates } from '~/hooks/useProductTemplates';

interface IndividualAuthFormProps {
  keyName: string;
  basePath: string;
  record: Solution;
}

export const IndividualAuthForm: FC<IndividualAuthFormProps> = ({ keyName, record }) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`featuresConfig.${key}`));
  const td = (key: string): string => translate(`utils.${key}`);

  const classes = useStyles();

  const form = useForm();

  const { data: productsTemplate } = useProductTemplates();
  const productsTemplateMap: Record<string, ProductTemplate> | undefined = useMemo(
    () => productsTemplate?.reduce((prev, cur) => Object.assign(prev, { [cur.id]: cur }), {}),
    [productsTemplate]
  );
  const { data: products, isLoading: productsLoading } = useProductsBySolutionId(record?.id);
  const productOptions = productsLoading
    ? []
    : products
        ?.filter(
          (product) => productsTemplateMap?.[product?.templateCode]?.category !== 'CNL_ACCOUNT'
        )
        .map((product) => ({
          id: product?.id,
          name: product?.name,
        }));
  return (
    <>
      <H3>{t('individualAuthTitle')}</H3>
      <BooleanInput
        label={t('enabled')}
        source={`${keyName}.secretWordSelfReset.enable`}
        className={classes.inputWidth}
      />
      <JsonInput
        label={t('questions')}
        source={`${keyName}.secretWordSelfReset.questions`}
        className={classes.schemaInput}
        validationError={t('jsonError')}
        validationEmptyError={td('jsonEmptyArrayObjectError')}
        initialValue='[]'
        // Set empty string so validation will force user to input something
        defaultValue=''
        // Make it always required for compatibility with backend
        required
      />
      <H3 className={classes.marginBottom}>{t('individualAuthDescription')}</H3>
      <Alert className={classes.marginBottom} severity='warning'>
        {t('warningIndividualAuth')}
      </Alert>
      <ArrayInput
        source={`${keyName}.secretWordSelfReset.maxBalanceProducts`}
        label=''
        className={classes.simpleArrayInput}
      >
        <SimpleFormIterator
          addButton={
            <Button startIcon={<AddCircleOutlineIcon />} color='default'>
              {t('addProduct')}
            </Button>
          }
        >
          <FormDataConsumer>
            {({ getSource, scopedFormData }: any) => {
              return (
                <>
                  <SelectInput
                    label={t('product')}
                    source={getSource('productId')}
                    choices={productOptions}
                    validate={required()}
                    loading={productsLoading}
                    className={classes.inputWidth}
                    onChange={(e: any) => {
                      const selectedProduct = products?.find(
                        (product) => product.id === e.target?.value
                      );
                      const currencySource = getSource('maxBalanceAmount.currency');
                      if (selectedProduct) {
                        form.change(currencySource, selectedProduct?.currency);
                      }
                    }}
                  />
                  <div className={classes.inputWidth}>
                    <AmountInput
                      positiveOnly
                      nonNullable
                      currencyDisabled
                      source={getSource('maxBalanceAmount')}
                      permanentSelectedCurrency={scopedFormData?.currency}
                      required
                      maxNumber={MAX_BALANCE}
                    />
                  </div>
                </>
              );
            }}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

const MAX_BALANCE = 100000000000;
